import { type Locale, navLabel } from '@kovix/shell-kit/i18n';
import { Link } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import type { NavGroup } from '@/shared/config/navigation';
import { NavIcon, ShellIcon } from './NavIcon';
import { groupKey, isActivePath, shellText } from './shellText';

/**
 * Menu trái theo mockup v152 (`.nav-section` / `.nav-group-toggle` / `.nav-group-count`): mục gốc luôn hiện; mỗi nhóm có tiêu đề viết hoa,
 * badge SỐ MỤC ĐÃ LỌC THEO QUYỀN và mũi tên thu/mở. Nhóm chứa màn đang mở tự mở (như `NAV_STATE.openGroups` của mockup); chưa khớp màn nào → mở nhóm đầu.
 * Dữ liệu vẫn là `nav` đã qua `filterNav` (routes.json) — component chỉ đổi cách hiển thị. Trạng thái mở nhóm chỉ trong phiên (không lưu trình duyệt).
 * Desktop thu gọn (`sidebar-collapsed`): CSS hiện mọi mục dạng icon, ẩn tiêu đề nhóm.
 */
/** Tổng số mục (ngoài mục gốc) tối đa để mở sẵn mọi nhóm. */
const SHORT_MENU_ITEMS = 18;

export function SidebarNav({
  nav,
  pathname,
  locale,
  collapsed,
}: {
  nav: NavGroup[];
  pathname: string;
  locale: Locale;
  collapsed: boolean;
}) {
  const activeGroup = nav.find((g) => g.group && g.items.some((i) => isActivePath(pathname, i.path)));
  const firstGroup = nav.find((g) => g.group);
  // Usability test 01/10/2026: vai trò có menu ngắn (kế toán, thủ kho… ≤ 18 mục) thấy MỌI nhóm mở sẵn — trước đây chỉ nhóm đầu mở nên
  // "Thanh toán" của kế toán nằm trong nhóm thu gọn. Menu dài (quản trị) giữ cách cũ: nhóm đang mở + nhóm đầu.
  const groups = nav.filter((g) => g.group);
  const shortMenu = groups.reduce((n, g) => n + g.items.length, 0) <= SHORT_MENU_ITEMS;
  const [open, setOpen] = useState<Set<string>>(() => {
    if (shortMenu) return new Set(groups.map(groupKey));
    const start = activeGroup ?? firstGroup;
    return new Set(start ? [groupKey(start)] : []);
  });
  // Đổi màn → mở nhóm chứa màn đó (không đóng nhóm người dùng đã mở)
  const activeKey = activeGroup ? groupKey(activeGroup) : null;
  useEffect(() => {
    if (!activeKey) return;
    setOpen((s) => (s.has(activeKey) ? s : new Set(s).add(activeKey)));
  }, [activeKey]);
  // Menu nạp sau khi phiên sẵn sàng (lúc đầu `nav` rỗng): chưa mở nhóm nào và không khớp màn nào → mở nhóm đầu, một lần
  const seeded = useRef(false);
  const firstKey = firstGroup ? groupKey(firstGroup) : null;
  const allKeys = groups.map(groupKey).join('|');
  useEffect(() => {
    if (seeded.current || !firstKey) return;
    seeded.current = true;
    setOpen((s) => (s.size > 0 ? s : new Set(shortMenu ? allKeys.split('|') : [firstKey])));
  }, [firstKey, shortMenu, allKeys]);

  const toggle = (k: string) =>
    setOpen((s) => {
      const n = new Set(s);
      if (n.has(k)) n.delete(k);
      else n.add(k);
      return n;
    });

  return (
    <nav className="kx-nav kx-shell-nav" aria-label="Điều hướng chính">
      {nav.map((g) => {
        const k = groupKey(g);
        const root = !g.group;
        const isOpen = root || open.has(k);
        const listId = `kx-nav-${k.replace(/[^\w-]+/g, '-')}`;
        return (
          <div key={k} className={`kx-shell-section${root ? ' root' : ''}${isOpen ? ' open' : ''}`}>
            {g.group ? (
              <button
                type="button"
                className="kx-shell-group"
                aria-expanded={isOpen}
                aria-controls={listId}
                title={shellText('groupToggle', locale, { group: g.group })}
                onClick={() => toggle(k)}
              >
                <span className="kx-shell-group-label">{g.group}</span>
                <span className="kx-shell-group-count">
                  {g.items.length}
                  <span className="kx-shell-sr"> {shellText('itemsUnit', locale)}</span>
                </span>
                <ShellIcon name="chevronRight" size={14} className="kx-shell-chevron" />
              </button>
            ) : null}
            <div className="kx-shell-items" id={listId}>
              {g.items.map((it) => {
                const active = isActivePath(pathname, it.path);
                const label = navLabel(it.key, it.label, locale);
                return (
                  <Link
                    key={it.key}
                    to={it.path as '/'}
                    className={`kx-nav-item kx-shell-item${active ? ' active' : ''}`}
                    aria-current={active ? 'page' : undefined}
                    title={collapsed ? label : it.tag ? `${label} · ${it.tag}` : undefined}
                  >
                    <NavIcon name={it.icon} className="ico" />
                    <span className="lbl">{label}</span>
                  </Link>
                );
              })}
            </div>
          </div>
        );
      })}
    </nav>
  );
}
